/**
 * AI Intake apply-matcher — Derek / Sun Auto demo slice.
 *
 * Pure conversion from extract-intake's Extraction payload into Create
 * Ticket job rows + an advisor-visible unmatched list. Lives outside
 * CreateTicket.tsx so vitest can exercise it without rendering the
 * page component.
 *
 * Tiers (in order of priority):
 *   1. code           — Gemini-emitted, server-verified catalog code.
 *   2. label          — normalized exact match against catalog label.
 *   3. catalog_label  — server-populated canonical label from extract.
 *   4. alias          — case-insensitive containment over jobs_catalog.aliases
 *                       (mirrors intake-attach-jobs Tier 3).
 *   5. synonym        — local in-file map for demo-critical broad phrases
 *                       (e.g. "oil change" → MAIN-OILFILTE-7F6BE4-GEN).
 *
 * Two passes:
 *   a. Walk `extraction.suggested_jobs` (Gemini-recommended).
 *   b. Walk `extraction.requested_services` (customer-explicit). Some
 *      phrasings — "oil change", "tire rotation" — get routed by Gemini
 *      into requested_services instead of suggested_jobs; without this
 *      pass they would be silently dropped.
 *
 * Boundary: the matcher only DECIDES what to add. It never creates
 * tickets, schedules, calls Supabase, runs OptiPlace, or assigns
 * techs/bays. Submit / scheduling / approve remain owned by
 * CreateTicket.tsx.
 */

import type {
  Extraction,
  ExtractedSuggestedJob,
} from '@/components/tickets/AiIntakeAssistDialog';

// ===================================================================
// Public types
// ===================================================================

/**
 * Loosely-typed jobs_catalog row as it lands in CreateTicket.allJobs.
 * The page does a `select('*')` so the shape is whatever the table
 * returns; this interface lists the fields the matcher actually reads.
 */
export interface AllJobRow {
  id?: string;
  code?: string | null;
  label?: string | null;
  category_key?: string;
  sub_menu?: string;
  description?: string | null;
  aliases?: unknown;
  minutes_default?: number | null;
  skill_level?: string | null;
  proficiency_category?: string | null;
}

/**
 * Shape pushed into CreateTicket.selectedJobs. Mirrors the inline
 * object the page used to construct from addJobFromCatalog.
 */
export interface SelectedJobRow {
  tempJobId: string;
  category: string | undefined;
  submenu: string | undefined;
  name: string;
  hours: number;
  bay: string;
  skill: number;
  proficiency_category?: string | null;
  catalogJobId?: string;
  customHours?: number;
  notes?: string;
}

export interface ApplyMatcherInput {
  extraction: Extraction;
  allJobs: AllJobRow[];
  currentSelectedJobs: Array<{ name: string }>;
  synonymMap?: Record<string, string>;
  broadSynonyms?: Set<string>;
  /** Override id generation in tests so snapshots are stable. */
  tempIdFn?: () => string;
}

export interface ApplyMatcherResult {
  matchedAdditions: SelectedJobRow[];
  unmatched: ExtractedSuggestedJob[];
}

// ===================================================================
// Normalization
// ===================================================================

/**
 * Canonical match key. Lowercases, expands `&` → `and`, strips
 * punctuation / em-dashes / extra whitespace. Same input → same key
 * regardless of casing or surface punctuation.
 *
 * Catalog labels like "Engine Oil & Filter — Conventional Oil Change"
 * normalize to "engine oil and filter conventional oil change".
 */
export function normalizeMatchKey(s: unknown): string {
  if (typeof s !== 'string') return '';
  return s
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ===================================================================
// Defaults
// ===================================================================

/**
 * Demo-critical synonym map. Keys are normalized intake phrases; values
 * are jobs_catalog codes. The matcher resolves the code against the
 * actual loaded `allJobs`, so missing rows silently fall through to
 * unmatched (never invents a job).
 *
 * Oil change defaults to Full Synthetic — safest modern default;
 * advisor can still change/replace before submitting.
 */
export const DEFAULT_INTAKE_SYNONYM_MAP: Record<string, string> = {
  // --- Oil change (maintenance) ------------------------------------
  'oil change': 'MAIN-OILFILTE-7F6BE4-GEN',
  'oil and filter': 'MAIN-OILFILTE-7F6BE4-GEN',
  'engine oil': 'MAIN-OILFILTE-7F6BE4-GEN',
  'oil service': 'MAIN-OILFILTE-7F6BE4-GEN',
  'lube oil filter': 'MAIN-OILFILTE-7F6BE4-GEN',
  'full synthetic oil change': 'MAIN-OILFILTE-7F6BE4-GEN',
  'synthetic oil change': 'MAIN-OILFILTE-7F6BE4-GEN',

  // --- Cooling system + water pump (engine_mechanical) -------------
  // Specific 1:1 resolutions.
  'water pump': 'ENGI-COOLIN-45FF21-RR',
  'water pump replacement': 'ENGI-COOLIN-45FF21-RR',
  'radiator': 'ENGI-COOLIN-C9795C-RR',
  'radiator replacement': 'ENGI-COOLIN-C9795C-RR',
  'cooling system inspection': 'INSP-COOLING-SYS',
  'coolant leak': 'INSP-COOLING-SYS',
  // Belts & hoses / "cooling system" are broad phrases with no umbrella
  // catalog row. Map to Cooling System Inspection so the advisor lands
  // on a safe starting point, and ALSO re-surface the original phrase
  // (see broadSynonyms) so they pick specifics. (Ampersands normalize
  // to "and", so "belts & hoses" matches the same key.)
  'belts and hoses': 'INSP-COOLING-SYS',
  'belts and hoses replaced': 'INSP-COOLING-SYS',
  'belts and hoses replacement': 'INSP-COOLING-SYS',
  'cooling system': 'INSP-COOLING-SYS',

  // --- Brakes ------------------------------------------------------
  // Specific axle jobs resolve 1:1; bare "brakes" is broad.
  'front brakes': 'BRK-FRONT-PADS-ROTORS',
  'front brake job': 'BRK-FRONT-PADS-ROTORS',
  'front pads and rotors': 'BRK-FRONT-PADS-ROTORS',
  'rear brakes': 'BRK-REAR-PADS-ROTORS',
  'rear brake job': 'BRK-REAR-PADS-ROTORS',
  'rear pads and rotors': 'BRK-REAR-PADS-ROTORS',
  'brakes': 'INSP-BRAKES',
  'brake job': 'INSP-BRAKES',
  'brake service': 'INSP-BRAKES',
  'brake inspection': 'INSP-BRAKES',

  // --- Inspections -------------------------------------------------
  'inspection': 'INSP-CVI',
  'vehicle inspection': 'INSP-CVI',
  'multi point inspection': 'INSP-CVI',
  'multipoint inspection': 'INSP-CVI',

  // --- Tires -------------------------------------------------------
  'tire rotation': 'TIRE-3B8735-ROT',
  'rotate tires': 'TIRE-3B8735-ROT',
  'mount and balance': 'TIRE-A89D00-MB',
  'tire mount and balance': 'TIRE-A89D00-MB',
  'tpms': 'TIRE-399E87-PRG',
  'tpms sensor': 'TIRE-399E87-PRG',
  'tpms relearn': 'TIRE-399E87-PRG',
  'tire repair': 'TIRE-BF1174-RPR',
  'flat repair': 'TIRE-BF1174-RPR',
  'patch tire': 'TIRE-BF1174-RPR',
  'tires': 'TIRE-A89D00-MB',
  'new tires': 'TIRE-A89D00-MB',
  'replace tires': 'TIRE-A89D00-MB',

  // --- Modules / Programming + PCM/ECM (electrical) ----------------
  // Specific modules resolve 1:1; bare "programming" is broad.
  'pcm': 'BF-PCM-RR-101',
  'powertrain control module': 'BF-PCM-RR-101',
  'pcm programming': 'BF-PCM-PRG-100',
  'pcm flash': 'BF-PCM-PRG-100',
  'reflash pcm': 'BF-PCM-PRG-100',
  'ecm': 'BF-ECM-RR-103',
  'engine control module': 'BF-ECM-RR-103',
  'ecm programming': 'BF-ECM-PRG-102',
  'ecm flash': 'BF-ECM-PRG-102',
  'tcm': 'BF-TCM-RR-105',
  'transmission control module': 'BF-TCM-RR-105',
  'bcm': 'BF-BCM-RR-002',
  'body control module': 'BF-BCM-RR-002',
  // Door Control Module: bare name resolves to R&R (as BCM/PCM/ECM do);
  // "programming"/"flash" resolves to Program.
  'dcm': 'BF-DCM-RR-115',
  'door module': 'BF-DCM-RR-115',
  'door control module': 'BF-DCM-RR-115',
  'door module replacement': 'BF-DCM-RR-115',
  'replace door module': 'BF-DCM-RR-115',
  'door module programming': 'BF-DCM-PRG-114',
  'door control module programming': 'BF-DCM-PRG-114',
  'door module flash': 'BF-DCM-PRG-114',
  'program door module': 'BF-DCM-PRG-114',
  'dcm programming': 'BF-DCM-PRG-114',
  'module programming': 'BF-ECM-PRG-102',
  'reprogram': 'BF-ECM-PRG-102',
  'reflash': 'BF-ECM-PRG-102',

  // --- Detailing (detailing) --------------------------------------
  'car wash': 'DTL-WASH-HAND',
  'hand wash': 'DTL-WASH-HAND',
  'wash': 'DTL-WASH-DRY',
  'full detail': 'DTL-FULL-DETAIL',
  'complete detail': 'DTL-FULL-DETAIL',
  'vacuum': 'DTL-INT-VAC',
  'ceramic coat': 'DTL-PAINT-CERAMIC',

  // --- Drivability / diagnostics (diagnostics) ---------------------
  'rough idle': 'DIAG-MISFIRE-ROUGH-IDLE',
  'misfire': 'DIAG-MISFIRE-ROUGH-IDLE',
  'engine misfire': 'DIAG-MISFIRE-ROUGH-IDLE',
  'no start': 'DIAG-NO-CRANK-NO-START',
  'will not start': 'DIAG-NO-CRANK-NO-START',
  'no crank': 'DIAG-NO-CRANK-NO-START',
  'stalling': 'DIAG-STALLING',
  'engine stalls': 'DIAG-STALLING',
  'hard start': 'DIAG-HARD-START',
  'long crank': 'DIAG-HARD-START',
  // "Check engine light" / generic "diagnostic" have no single catalog
  // target — anchor to a powertrain drivability diagnosis and re-surface.
  'check engine light': 'DIAG-MISFIRE-ROUGH-IDLE',
  'cel': 'DIAG-MISFIRE-ROUGH-IDLE',
  'warning light': 'DIAG-MISFIRE-ROUGH-IDLE',
  'drivability': 'DIAG-MISFIRE-ROUGH-IDLE',
  'diagnostic': 'DIAG-MISFIRE-ROUGH-IDLE',
  'diagnosis': 'DIAG-MISFIRE-ROUGH-IDLE',
};

/**
 * Synonym keys whose mapping is an umbrella / safe-starting-point
 * rather than a 1:1 service swap. For these, the matcher still adds the
 * mapped row AND re-surfaces the original phrase to the advisor-visible
 * banner so they can add the specific item(s) needed. Keep narrow,
 * confident terms (water pump, tire rotation, PCM, misfire) OUT of this
 * set — only genuinely broad asks belong here.
 */
export const DEFAULT_BROAD_SYNONYMS = new Set<string>([
  'belts and hoses',
  'belts and hoses replaced',
  'belts and hoses replacement',
  'cooling system',
  'brakes',
  'brake job',
  'brake service',
  'brake inspection',
  'inspection',
  'vehicle inspection',
  'multi point inspection',
  'multipoint inspection',
  'tires',
  'new tires',
  'replace tires',
  'module programming',
  'reprogram',
  'reflash',
  'check engine light',
  'cel',
  'warning light',
  'drivability',
  'diagnostic',
  'diagnosis',
]);

const SKILL_LEVEL_MAP: Record<string, number> = {
  basic: 1,
  intermediate: 2,
  advanced: 3,
  expert: 4,
};

// ===================================================================
// Internal helpers
// ===================================================================

function defaultTempIdFn(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `tmp_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

type MatchHit = {
  row: AllJobRow;
  via: 'code' | 'label' | 'catalog_label' | 'alias' | 'synonym';
};

function findCatalogMatch(args: {
  name: string;
  code: string | null;
  catalog_label: string | null;
  allJobs: AllJobRow[];
  synonymMap: Record<string, string>;
}): MatchHit | null {
  const { allJobs, synonymMap } = args;
  const nCode = normalizeMatchKey(args.code);
  const nName = normalizeMatchKey(args.name);
  const nCatalog = normalizeMatchKey(args.catalog_label);

  // Tier 1: code — Gemini-emitted, server-verified catalog code wins.
  if (nCode) {
    const byCode = allJobs.find((j) => normalizeMatchKey(j.code) === nCode);
    if (byCode) return { row: byCode, via: 'code' };
  }
  // Tier 2: normalized label exact.
  if (nName) {
    const byLabel = allJobs.find((j) => normalizeMatchKey(j.label) === nName);
    if (byLabel) return { row: byLabel, via: 'label' };
  }
  // Tier 3: normalized catalog_label exact.
  if (nCatalog) {
    const byCatalogLabel = allJobs.find((j) => normalizeMatchKey(j.label) === nCatalog);
    if (byCatalogLabel) return { row: byCatalogLabel, via: 'catalog_label' };
  }
  // Tier 4: alias containment.
  if (nName || nCatalog) {
    const byAlias = allJobs.find((j) => {
      if (!Array.isArray(j.aliases)) return false;
      return (j.aliases as unknown[]).some((a) => {
        const na = normalizeMatchKey(a);
        return na !== '' && (na === nName || na === nCatalog);
      });
    });
    if (byAlias) return { row: byAlias, via: 'alias' };
  }
  // Tier 5: local synonym → code → allJobs.
  for (const key of [nName, nCatalog]) {
    if (!key) continue;
    const target = synonymMap[key];
    if (!target) continue;
    const bySyn = allJobs.find((j) => normalizeMatchKey(j.code) === normalizeMatchKey(target));
    if (bySyn) return { row: bySyn, via: 'synonym' };
  }
  return null;
}

function buildSelectedJobRow(
  matched: AllJobRow,
  estimatedMinutes: number | null | undefined,
  rationale: string | undefined,
  tempIdFn: () => string,
): SelectedJobRow {
  // Treat 0 / negative / non-finite as MISSING (not a valid duration).
  // Plain `??` would let Gemini's frequent `estimated_minutes: 0` for
  // generic services (oil change, inspections) land as 0.0 hrs in the
  // form. Same pattern extract-intake already uses for Gemini minutes
  // in handler.ts:applyCatalogTruth.
  const validatedEstimate =
    typeof estimatedMinutes === 'number' &&
    Number.isFinite(estimatedMinutes) &&
    estimatedMinutes > 0
      ? estimatedMinutes
      : null;
  const validatedCatalog =
    typeof matched.minutes_default === 'number' &&
    Number.isFinite(matched.minutes_default) &&
    matched.minutes_default > 0
      ? matched.minutes_default
      : null;
  const minutes = validatedEstimate ?? validatedCatalog ?? 60;
  const hours = Math.round((minutes / 60) * 10) / 10;
  let skill = SKILL_LEVEL_MAP[normalizeMatchKey(matched.skill_level)] || 2;
  if (matched.category_key === 'diagnostics' && skill === 3) skill = 4;
  return {
    tempJobId: tempIdFn(),
    category: matched.category_key,
    submenu: matched.sub_menu,
    name: matched.label ?? '',
    hours,
    bay: '',
    skill,
    proficiency_category: matched.proficiency_category ?? null,
    catalogJobId: matched.id,
    customHours: undefined,
    notes: rationale ? `AI: ${rationale}` : undefined,
  };
}

function extractRequestedServiceFields(rs: unknown): {
  name: string;
  category: string | null;
  urgency: string | null;
} {
  if (typeof rs === 'string') {
    return { name: rs.trim(), category: null, urgency: null };
  }
  if (rs && typeof rs === 'object') {
    const o = rs as Record<string, unknown>;
    const pick = (k: string) => (typeof o[k] === 'string' ? (o[k] as string).trim() : '');
    const name = pick('name') || pick('service') || pick('label') || pick('description');
    return {
      name,
      category: typeof o.category === 'string' ? (o.category as string) : null,
      urgency: typeof o.urgency === 'string' ? (o.urgency as string) : null,
    };
  }
  return { name: '', category: null, urgency: null };
}

function makeBroadSynonymBannerEntry(
  originalName: string,
  mappedLabel: string | null | undefined,
  category: string | null,
): ExtractedSuggestedJob {
  return {
    name: originalName,
    category,
    estimated_minutes: null,
    skill_level: null,
    rationale: `Customer asked for "${originalName}". Mapped to "${mappedLabel ?? ''}" — review the job picker for specific items.`,
    code: null,
    catalog_label: null,
    category_key: null,
    sub_menu: null,
    match_confidence: 'none',
    match_method: 'none',
  };
}

// ===================================================================
// Public entry point
// ===================================================================

export function matchExtractionToCatalog({
  extraction,
  allJobs,
  currentSelectedJobs,
  synonymMap = DEFAULT_INTAKE_SYNONYM_MAP,
  broadSynonyms = DEFAULT_BROAD_SYNONYMS,
  tempIdFn = defaultTempIdFn,
}: ApplyMatcherInput): ApplyMatcherResult {
  const matchedAdditions: SelectedJobRow[] = [];
  const unmatched: ExtractedSuggestedJob[] = [];

  // Dedup state shared across both passes so requested_services can't
  // re-add what suggested_jobs already landed.
  const alreadyByLabel = new Set(
    currentSelectedJobs.map((j) => normalizeMatchKey(j.name)),
  );
  const alreadyByCode = new Set<string>();

  function tryApply(
    matched: AllJobRow,
    estimatedMinutes: number | null | undefined,
    rationale: string | undefined,
  ): boolean {
    const lblKey = normalizeMatchKey(matched.label);
    const codeKey = normalizeMatchKey(matched.code);
    if (lblKey && alreadyByLabel.has(lblKey)) return false;
    if (codeKey && alreadyByCode.has(codeKey)) return false;
    matchedAdditions.push(
      buildSelectedJobRow(matched, estimatedMinutes, rationale, tempIdFn),
    );
    if (lblKey) alreadyByLabel.add(lblKey);
    if (codeKey) alreadyByCode.add(codeKey);
    return true;
  }

  // ---------- Pass 1: suggested_jobs ----------
  for (const sj of extraction.suggested_jobs ?? []) {
    const sjName = (sj.name ?? '').trim();
    if (!sjName) continue;

    const hit = findCatalogMatch({
      name: sjName,
      code: (sj.code ?? null) as string | null,
      catalog_label: (sj.catalog_label ?? null) as string | null,
      allJobs,
      synonymMap,
    });

    if (hit) {
      const added = tryApply(hit.row, sj.estimated_minutes ?? null, sj.rationale);
      // Broad synonym (e.g. belts and hoses) → also re-surface the
      // original phrase to the advisor banner so they can pick the
      // specific belts/hoses needed.
      if (
        added &&
        hit.via === 'synonym' &&
        broadSynonyms.has(normalizeMatchKey(sjName))
      ) {
        unmatched.push(
          makeBroadSynonymBannerEntry(sjName, hit.row.label, sj.category ?? null),
        );
      }
    } else {
      unmatched.push(sj);
    }
  }

  // ---------- Pass 2: requested_services (catch what Gemini didn't route through suggested_jobs) ----------
  for (const rs of (extraction.requested_services ?? []) as unknown[]) {
    const { name, urgency } = extractRequestedServiceFields(rs);
    if (!name) continue;

    const hit = findCatalogMatch({
      name,
      code: null,
      catalog_label: null,
      allJobs,
      synonymMap,
    });

    if (hit) {
      const rationale = `Customer requested${urgency ? ` (${urgency})` : ''}: ${name}`;
      const added = tryApply(hit.row, null, rationale);
      if (
        added &&
        hit.via === 'synonym' &&
        broadSynonyms.has(normalizeMatchKey(name))
      ) {
        unmatched.push(makeBroadSynonymBannerEntry(name, hit.row.label, null));
      }
      // Already-added (dedup) cases drop silently — don't double-banner
      // a duplicate request.
    } else {
      // No match — keep it advisor-visible rather than silently dropped.
      unmatched.push({
        name,
        category: null,
        estimated_minutes: null,
        skill_level: null,
        rationale: `Customer requested${urgency ? ` (${urgency})` : ''}: ${name}`,
        code: null,
        catalog_label: null,
        category_key: null,
        sub_menu: null,
        match_confidence: 'none',
        match_method: 'none',
      });
    }
  }

  return { matchedAdditions, unmatched };
}
